// Vendor Libs
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';

// Dish Tech React
import {useForm, useService, useToast} from '@dishtech-react/hooks';
import {InputTextField} from '@dishtech-react/core';

// Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import {Button, FormControl, FormControlLabel, InputLabel, Select, Switch} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';

// Components
import RoleTree from './role-tree';

// Config
import {serviceConfig} from '../../../config/service-config';

const useStyles = makeStyles(theme => ({
    button: {
        margin: theme.spacing(2, 1),
    },
    border: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    formControlLabel: {
        display: 'block',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    select: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
}));

const FORM_KEY = 'ROLE';

const RoleForm = ({mode, onCancel, onSave}) => {
    const classes = useStyles();
    const {
        formValid,
        formValues,
        formValues: {roleId = '', name = '', admin = false, childRoleIds = [], clientId},
        registerForm,
        setFieldValue,
        resetForm,
        submitForm,
    } = useForm(serviceConfig, FORM_KEY, mode);
    const {createToast} = useToast();

    useEffect(() => {
        registerForm();

        return () => resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = () => {
        submitForm(formValues, mode === 'update' ? roleId : undefined).then(({error}) => {
            if (error) {
                createToast(error, {
                    variant: 'error',
                    persist: true,
                    onClose: () => onSave(),
                });
            } else {
                createToast(`Successfully ${mode}d role.`);
                onSave();
            }
        });
    };

    const {
        serviceContent: {content: roleData = []},
    } = useService('ROLE');

    const {
        serviceContent: {content: clientData = []},
    } = useService('CLIENT');

    return (
        <>
            <InputTextField
                autoFocus={false}
                className={classes.border}
                fullWidth={true}
                id="name"
                label="Name"
                margin="dense"
                multiline={true}
                type="text"
                value={name}
                onChange={event => setFieldValue('name', event.target.value)}
            />

            <FormControl className={classes.border} required={true}>
                <InputLabel htmlFor="client">Client</InputLabel>
                <Select
                    native={true}
                    value={clientId}
                    onChange={event => setFieldValue('clientId', event.target.value)}
                >
                    <option value=""> </option>
                    {clientData
                        ? clientData.map(({clientId: cId}) => (
                              <option key={cId} value={cId}>
                                  {cId}
                              </option>
                          ))
                        : undefined}
                </Select>
            </FormControl>

            <FormControlLabel
                className={classes.formControlLabel}
                control={
                    <Switch
                        checked={admin}
                        color="primary"
                        id="admin"
                        value={admin}
                        onChange={event => setFieldValue('admin', event.target.checked)}
                    />
                }
                label={`${admin ? '' : 'Not '}Admin`}
            />

            <RoleTree
                editedRoleId={mode === 'update' ? roleId : undefined}
                label="Child Roles"
                roleData={roleData.filter(role => role.client.clientId === clientId)}
                selectedRoles={childRoleIds}
                onChange={value => setFieldValue('childRoleIds', value)}
            />

            <Button
                className={classes.button}
                color="default"
                startIcon={<CancelIcon />}
                variant="contained"
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                className={classes.button}
                color="primary"
                disabled={!formValid}
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
            >
                {mode}
            </Button>
        </>
    );
};

RoleForm.propTypes = {
    mode: PropTypes.string,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

RoleForm.defaultProps = {
    mode: 'create',
};

export default RoleForm;
